// Режимы рабочей области: презентация/редактор, боковые панели, выбранный виджет.

import type { VisualizationType, WidgetDefinition } from '../types';
import { t } from '../i18n';
import { isAnonymous } from '../auth';

import { controls } from './controls';
import { currentLocale, setIconButton } from './locale';
import { findWidget } from './widgets';
import { loadIndicatorSections, populateIndicators } from './indicators';
import { narrowWorkspaceMedia, phoneWorkspaceMedia, scheduleCanvasRefresh, setLayoutEditing } from './canvas';
import { setStatus } from './status';
import { state } from './state';
import { updateIndicatorControls } from './widget-form';

export function syncModeSegment(): void {
  controls.viewModeButton.setAttribute('aria-pressed', String(state.viewMode));
  const label = state.viewMode ? t(currentLocale(), 'enterEditMode') : t(currentLocale(), 'enterViewMode');
  setIconButton(controls.toggleViewMode, label, state.viewMode ? 'pencil' : 'eye');
  controls.toggleViewMode.classList.toggle('active', !state.viewMode);
  controls.toggleViewMode.setAttribute('aria-pressed', String(!state.viewMode));
}

export function syncFilterbarVisibility(): void {
  const hasWidgets = state.definition.widgets.length > 0;
  const visible = hasWidgets && state.filtersOpen;
  controls.dashboardFilterbar.hidden = !visible;
  controls.toggleFilterbar.setAttribute('aria-expanded', String(visible));
  controls.toggleFilterbar.classList.toggle('active', visible);
}

export function setFilterbarOpen(open: boolean): void {
  state.filtersOpen = open;
  syncFilterbarVisibility();
}

export function setSourcesMenuOpen(open: boolean): void {
  state.sourcesMenuOpen = open;
  controls.sourcesMenu.hidden = !open;
  controls.sourcesMenuButton.setAttribute('aria-expanded', String(open));
  controls.sourcesMenuButton.classList.toggle('active', open);
}

export function setBuilderOpen(open: boolean): void {
  if (state.viewMode && open) open = false;
  if (open && !selectedWidget()) {
    open = false;
    setStatus(t(currentLocale(), 'selectWidget'));
  }
  state.builderOpen = open;
  controls.applicationShell.classList.toggle('builder-open', open);
  controls.applicationShell.classList.toggle('builder-collapsed', !open);
  controls.applicationShell.classList.toggle('inspector-open', open);
  controls.applicationShell.classList.toggle('inspector-collapsed', !open);
  controls.toggleBuilder.setAttribute('aria-expanded', String(open));
  controls.toggleBuilder.setAttribute('aria-label', t(currentLocale(), 'showInspector'));
  controls.toggleBuilder.dataset.tooltip = t(currentLocale(), 'showInspector');
  setIconButton(controls.closeBuilder, t(currentLocale(), 'closePanel'), 'x');
  if (open && narrowWorkspaceMedia.matches && state.paletteOpen) setPaletteOpen(false, false);
  syncDrawerLock();
  scheduleCanvasRefresh();
}

export function setPaletteOpen(open: boolean, updateGrid = true): void {
  if (state.viewMode && open) open = false;
  state.paletteOpen = open;
  controls.applicationShell.classList.toggle('palette-open', open);
  controls.applicationShell.classList.toggle('palette-collapsed', !open);
  controls.togglePalette.setAttribute('aria-expanded', String(open));
  controls.showPalette.setAttribute('aria-expanded', String(open));
  controls.togglePalette.setAttribute('aria-label', t(currentLocale(), 'closePanel'));
  controls.togglePalette.dataset.tooltip = t(currentLocale(), 'closePanel');
  controls.showPalette.setAttribute('aria-label', 'Виджет');
  controls.showPalette.dataset.tooltip = 'Виджет';
  if (open && narrowWorkspaceMedia.matches && state.builderOpen) setBuilderOpen(false);
  syncDrawerLock();
  if (updateGrid) scheduleCanvasRefresh();
}

export function syncDrawerLock(): void {
  const builderDrawerOpen = state.builderOpen && narrowWorkspaceMedia.matches;
  const paletteDrawerOpen = state.paletteOpen && narrowWorkspaceMedia.matches;
  document.body.classList.toggle('builder-drawer-open', builderDrawerOpen);
  document.body.classList.toggle('palette-drawer-open', paletteDrawerOpen);
  document.documentElement.classList.toggle('drawer-open', builderDrawerOpen || paletteDrawerOpen);
}

export function selectedWidget(): WidgetDefinition | undefined {
  return state.editingWidgetId ? findWidget(state.editingWidgetId) : undefined;
}

export function inspectorVisualization(): VisualizationType {
  return selectedWidget()?.visualization ?? state.inspectorVisualization ?? controls.visualization.value as VisualizationType;
}

export function syncSelectedWidgetClass(): void {
  controls.gridRoot.querySelectorAll<HTMLElement>('.grid-stack-item').forEach((item) => {
    const id = item.getAttribute('gs-id') ?? item.dataset.gsId ?? item.dataset.widgetId ?? item.querySelector<HTMLElement>('[data-widget-id]')?.dataset.widgetId;
    item.classList.toggle('is-selected-widget', Boolean(id && id === state.editingWidgetId));
  });
}

export function focusWidgetOnCanvas(id: string, flash = true): void {
  const item = controls.gridRoot.querySelector<HTMLElement>(`.grid-stack-item[gs-id="${CSS.escape(id)}"]`);
  if (!item) return;
  item.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
  if (!flash) return;
  item.classList.remove('is-focus-flash');
  void item.offsetWidth;
  item.classList.add('is-focus-flash');
  window.setTimeout(() => item.classList.remove('is-focus-flash'), 1800);
}

export function updateInspectorState(): void {
  const hasSelection = Boolean(selectedWidget());
  controls.inspectorEmpty.hidden = hasSelection;
  controls.appearanceInspector.hidden = !hasSelection;
  controls.addAsNewWidget.hidden = !hasSelection;
  controls.builderTitle.textContent = t(currentLocale(), 'properties');
  controls.toggleBuilder.disabled = !hasSelection;
  controls.toggleBuilder.dataset.tooltip = hasSelection ? t(currentLocale(), 'showInspector') : t(currentLocale(), 'selectWidget');
  controls.toggleBuilder.setAttribute('aria-label', hasSelection ? t(currentLocale(), 'showInspector') : t(currentLocale(), 'selectWidget'));
  if (!hasSelection && state.builderOpen) setBuilderOpen(false);
  setIconButton(
    controls.submitWidget,
    hasSelection ? t(currentLocale(), 'updateSelectedData') : t(currentLocale(), 'addToDashboard'),
    hasSelection ? 'refresh-cw' : 'plus',
    true,
  );
  setIconButton(controls.addAsNewWidget, t(currentLocale(), 'addAsNew'), 'copy-plus', true);
  syncSelectedWidgetClass();
}

export function clearSelectedWidget(closeInspector = true): void {
  state.editingWidgetId = null;
  state.inspectorVisualization = null;
  updateInspectorState();
  if (closeInspector) setBuilderOpen(false);
}

export function ensureBuilderStarterState(): void {
  if (selectedWidget() || controls.source.value) return;
  if (!state.indicators.some((indicator) => indicator.source_type === 'bdmo' || indicator.id.startsWith('bdmo:'))) return;
  controls.source.value = 'bdmo';
  void loadIndicatorSections().then(() => {
    populateIndicators();
    void updateIndicatorControls();
  });
}

export function setViewMode(enabled: boolean): void {
  // Гость (вход включён, но не выполнен) только смотрит.
  if (isAnonymous()) enabled = true;
  state.viewMode = enabled;
  controls.applicationShell.classList.toggle('presentation-mode', enabled);
  syncModeSegment();
  if (enabled) {
    setLayoutEditing(false);
    setBuilderOpen(false);
    setPaletteOpen(false);
  } else if (narrowWorkspaceMedia.matches) {
    setBuilderOpen(false);
    setPaletteOpen(false);
    setLayoutEditing(false);
    ensureBuilderStarterState();
  } else {
    setPaletteOpen(true);
    setBuilderOpen(Boolean(selectedWidget()));
    setLayoutEditing(!phoneWorkspaceMedia.matches);
    ensureBuilderStarterState();
  }
  scheduleCanvasRefresh();
  scheduleCanvasRefresh(520);
}
